/**
 * Where a release target stands in the stage machine, said once for every reader.
 *
 * The stage machine has one writer (`transitionTarget` in the backend), but three readers used to
 * keep their own copy of what each stage means: the backend's terminal set, the releases page's
 * "running" set and the fleet board's moving / done / stopped sets. They disagreed — the fleet board
 * left DELIVERING out of moving, counted a `COMPLETED` value the enum does not have, and left GATED,
 * DELIVERED and FAILED_TRANSIENT in no bucket at all. Every reader now asks this table.
 *
 * Imports the status type from `../../schemas/entities/mobile-deployment` directly, never `./index`:
 * `./index` re-exports this file.
 *
 * Stage machine: docs/platform-releases/data-model.md, "The target stage machine".
 */

import type { AppReleaseTargetStatus } from '../../schemas/entities/mobile-deployment'

/**
 * - `queued`: waiting for a worker to pick it up.
 * - `in_flight`: our pipeline or a provider is working on it right now (building, built and waiting
 *   for its submission, delivering to a store).
 * - `in_review`: the store has it and is reviewing it.
 * - `awaiting_release`: approved or delivered, NOT live. Apple holding a version for a developer
 *   release, or a target GATED on its go-live condition (a visual approval). Waiting on a person.
 * - `retrying`: a transient failure waiting for the next retry wave. Not finished.
 * - `released`: live. Terminal.
 * - `stopped`: blocked, refused, cancelled or failed. Terminal; a person owns it now.
 */
export type ReleaseTargetStage =
  'queued' | 'in_flight' | 'in_review' | 'awaiting_release' | 'retrying' | 'released' | 'stopped'

/**
 * Every target status, and its stage. A `Record` over the enum, so a status added to
 * `AppReleaseTargetStatus` without a stage here fails typecheck.
 *
 * The placements a reader would otherwise argue about, each taken from the transition rules:
 *  - BUILT is `in_flight`: a store-bound build is waiting for its submission, which the pipeline
 *    starts on its own; a build-only target never rests here (it advances straight to RELEASED).
 *  - DELIVERED is `awaiting_release`: it is what Apple's PENDING_DEVELOPER_RELEASE maps to, and its
 *    only forward edges are GATED and RELEASED — the store is done, the go-live decision is not.
 *  - GATED is `awaiting_release`: the go-live condition is unmet until someone approves it.
 *  - FAILED_TRANSIENT is `retrying`, NOT terminal: it rejoins the queue on the next retry wave (R19),
 *    so a release holding one has not finished.
 *  - SUBMITTING / SUBMITTED / IN_REVIEW / APPROVED are pre-collapse vocabulary rows still carry;
 *    they sit with the stage they meant (Apple's lane still produces SUBMITTED and IN_REVIEW).
 *  - FAILED is the pre-collapse terminal failure, kept terminal because rows carry it.
 */
export const RELEASE_TARGET_STAGES: Readonly<Record<AppReleaseTargetStatus, ReleaseTargetStage>> = {
  PENDING: 'queued',
  BUILDING: 'in_flight',
  BUILT: 'in_flight',
  DELIVERING: 'in_flight',
  SUBMITTING: 'in_flight',
  SUBMITTED: 'in_review',
  IN_REVIEW: 'in_review',
  APPROVED: 'awaiting_release',
  DELIVERED: 'awaiting_release',
  GATED: 'awaiting_release',
  FAILED_TRANSIENT: 'retrying',
  RELEASED: 'released',
  BLOCKED: 'stopped',
  REFUSED: 'stopped',
  CANCELLED: 'stopped',
  FAILED: 'stopped'
}

/** The stage of a target status. */
export function releaseTargetStage(status: AppReleaseTargetStatus): ReleaseTargetStage {
  return RELEASE_TARGET_STAGES[status]
}

/** The stages no event moves a target out of. */
const TERMINAL_STAGES: ReadonlySet<ReleaseTargetStage> = new Set(['released', 'stopped'])

/**
 * The statuses no event moves a target out of: RELEASED, BLOCKED, REFUSED, CANCELLED, FAILED. The
 * backend's transition rules and release rollup read this, so "terminal" cannot mean one thing to
 * the stage machine and another to the release that holds the target.
 */
export const TERMINAL_RELEASE_TARGET_STATUSES: readonly AppReleaseTargetStatus[] = (
  Object.keys(RELEASE_TARGET_STAGES) as AppReleaseTargetStatus[]
).filter(status => TERMINAL_STAGES.has(RELEASE_TARGET_STAGES[status]))
